/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import React, {CSSProperties, MouseEventHandler, useRef} from "react";
import './style/emailStepStats.scss';

import {DashboardPeriod, DataSplitMethod, EmailStepStat} from "../../../util/interfaces";
import {AwesomeIcon, ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';
import {BolmateButton, BolmateSelect} from "../../controls";
import PeriodPicker from "../../dashboard/periodPicker/periodPicker";
import classNames from "classnames";
import { parseThousandSeparator } from "../../../util/utils";

interface Props {
    onClose: MouseEventHandler;
    onChangePeriod: ChangeFunction<DashboardPeriod>;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    stats: EmailStepStat[];
    active: boolean;
    loading: boolean;
    period: DashboardPeriod | null;
    splitMethod: DataSplitMethod;
    showColors: boolean;
}

const AVERAGE_CTR = 2.3;

export default function EmailStepStats(props: Props) {
    const {
        stats, loading, active, splitMethod, period, onChangePeriod, onChangeSplitMethod, onClose, showColors,
    } = props;
    const methodItems: SelectItemShape[] = [
        {label: 'Per dag', value: DataSplitMethod.DAY},
        {label: 'Per week', value: DataSplitMethod.WEEK},
        {label: 'Per maand', value: DataSplitMethod.MONTH},
        {label: 'Per kwartaal', value: DataSplitMethod.QUARTER},
        {label: 'Per jaar', value: DataSplitMethod.YEAR},
    ];
    const periodPicker = useRef<PeriodPicker>(null);

    function onClickPeriod() {
        periodPicker.current?.activate();
    }

    function getPeriodHeadLabel() {
        switch (splitMethod) {
            case DataSplitMethod.WEEK:
                return 'Week';
            case DataSplitMethod.MONTH:
                return 'Maand';
            case DataSplitMethod.QUARTER:
                return 'Kwartaal';
            case DataSplitMethod.YEAR:
                return 'Jaar';
            default:
                return 'Datum';
        }
    }

    function calcPercentage(variable: number, comparedTo: number): string {
        if (!variable || !comparedTo) return '0';
        return ((variable / comparedTo) * 100).toFixed(2);
    }

    function getCTRStyle(ctrPercentage: number): CSSProperties | undefined {
        if (!showColors) return undefined;
        let color: string, fontWeight = 400;
        if (ctrPercentage > AVERAGE_CTR) {
            color = '#18A155'; // Green
            fontWeight = 600;
        } else if (ctrPercentage >= AVERAGE_CTR) color = '#18A155';
        else color = '#f39320';
        return {color, fontWeight};
    }

    function renderStats() {
        return (
            <div className='stats-container'>
                <Loader active={loading}/>
                <div className='row head'>
                    <label>{getPeriodHeadLabel()}</label>
                    <label className='center'>Aantal mails verzonden</label>
                    <label className='center'>Aantal mails geopend</label>
                    {showColors ? renderWithInfo('Aantal reviewlinks geklikt',
                            `In 2021 was wereldwijd de gemiddelde klik rate ${AVERAGE_CTR}% voor marketing mails.`, true) :
                        <label className='center'>Aantal reviewlinks geklikt</label>}
                </div>
                {stats.map(renderStat)}
            </div>
        )
    }

    function renderWithInfo(title: string, text: string, positionLeft: boolean = false) {
        return (
            <div className='with-info center'>
                <label>{title}</label>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <p className={classNames('info-text', positionLeft && 'left')}>{text}</p>
                </div>
            </div>
        )
    }

    function renderStat(stat: EmailStepStat, index: number) {
        const ctrPercentage = calcPercentage(stat.CLICK, stat.OPEN);
        return (
            <div className='row' key={index}>
                <label>{stat.period}</label>
                <label className='center'>{parseThousandSeparator(stat.SEND)}</label>
                <label className='center'>{parseThousandSeparator(stat.OPEN)} ({calcPercentage(stat.OPEN, stat.SEND)}%)</label>
                <label className='center'>{parseThousandSeparator(stat.CLICK)} (<span
                    style={getCTRStyle(Number(ctrPercentage))}>{ctrPercentage}%</span>)</label>
            </div>
        )
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='email-step-stats default-popup'>
                <div className='header-container'>
                    <label className='header-title'>E-mail statistieken</label>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    <div className='filter-container'>
                        <BolmateSelect value={splitMethod} onChange={onChangeSplitMethod} items={methodItems}
                                       label='Weergave' className='display-select'/>
                        <BolmateButton onClick={onClickPeriod} label='Periode aanpassen' secondary/>
                    </div>
                    {renderStats()}
                </div>
            </div>
            <PeriodPicker onSubmit={onChangePeriod} period={period} ref={periodPicker}/>
        </WithBackground>
    )
}